import { authorizedRequest } from "../../../services/apiClient";

export type LotDocumentType =
  | "title_scan"
  | "tax_declaration"
  | "survey_plan"
  | "payment_receipt"
  | "supporting_document"
  | "other";

export type LotDocumentStatus =
  | "pending"
  | "pending_scan"
  | "active"
  | "blocked"
  | "archived"
  | "expired";

export interface LotDocumentFile {
  id: string;
  bucket: string;
  storage_key: string;
  mime_type: string;
  byte_size: number;
}

export interface LotDocument {
  id: string;
  lot_id: string;
  file_id: string;
  attachment_key: string;
  document_type: LotDocumentType;
  revision: number;
  status: LotDocumentStatus;
  is_primary: boolean;
  description?: string;
  tags?: string[];
  filename?: string;
  uploaded_by?: string | null;
  uploaded_at: string;
  updated_at: string;
  file?: LotDocumentFile;
}

export interface LotDraftFile {
  id: string;
  draft_id: string;
  file_id: string;
  attachment_key: string;
  document_type: LotDocumentType;
  status: LotDocumentStatus;
  description?: string;
  tags?: string[];
  filename?: string;
  uploaded_by: string;
  uploaded_at: string;
  updated_at: string;
  expires_at: string;
  file?: LotDocumentFile;
}

export interface LotDocumentDraft {
  token: string;
  workspace_id: string;
  created_by: string;
  status: string;
  expires_at: string;
}

export interface CreateLotDocumentUploadRequest {
  document_type: LotDocumentType;
  attachment_key: string;
  filename: string;
  mime_type: string;
  byte_size: number;
  description?: string;
  tags?: string[];
  is_primary?: boolean;
}

export interface CompleteUploadRequest {
  upload_id: string;
  checksum?: string;
  byte_size: number;
  mime_type: string;
  filename: string;
  is_primary?: boolean;
  description?: string;
  tags?: string[];
}

export async function createLotDocumentDraft(accessToken: string, workspaceId: string) {
  return authorizedRequest<LotDocumentDraft>(accessToken, "/lots/drafts", {
    method: "POST",
    body: JSON.stringify({ workspace_id: workspaceId })
  });
}

export async function listDraftFiles(accessToken: string, draftToken: string) {
  return authorizedRequest<LotDraftFile[]>(accessToken, `/lots/drafts/${draftToken}/files`);
}

export async function presignDraftUpload(
  accessToken: string,
  draftToken: string,
  payload: CreateLotDocumentUploadRequest
) {
  return authorizedRequest<{ upload_id: string; storage_key: string; url: string; expires_at: string }>(
    accessToken,
    `/lots/drafts/${draftToken}/files/uploads`,
    { method: "POST", body: JSON.stringify(payload) }
  );
}

export async function listLotDocuments(accessToken: string, lotId: string) {
  return authorizedRequest<LotDocument[]>(accessToken, `/lots/${lotId}/files`);
}

export async function presignLotUpload(
  accessToken: string,
  lotId: string,
  payload: CreateLotDocumentUploadRequest
) {
  return authorizedRequest<{ upload_id: string; storage_key: string; url: string; expires_at: string }>(
    accessToken,
    `/lots/${lotId}/files/uploads`,
    { method: "POST", body: JSON.stringify(payload) }
  );
}

export async function completeUpload(accessToken: string, uploadId: string, payload: CompleteUploadRequest) {
  return authorizedRequest<{ document?: LotDocument; draft_file?: LotDraftFile }>(
    accessToken,
    `/lots/files/uploads/${uploadId}/complete`,
    { method: "POST", body: JSON.stringify(payload) }
  );
}

export async function downloadLotDocument(accessToken: string, lotId: string, docId: string) {
  return authorizedRequest<{ url: string }>(accessToken, `/lots/${lotId}/files/${docId}/download`, {
    method: "POST"
  });
}

export async function updateLotDocument(
  accessToken: string,
  lotId: string,
  docId: string,
  payload: { description?: string; tags?: string[]; is_primary?: boolean }
) {
  return authorizedRequest<LotDocument>(accessToken, `/lots/${lotId}/files/${docId}`, {
    method: "PATCH",
    body: JSON.stringify(payload)
  });
}

export async function deleteLotDocument(accessToken: string, lotId: string, docId: string) {
  return authorizedRequest<void>(accessToken, `/lots/${lotId}/files/${docId}`, {
    method: "DELETE"
  });
}
